import { App } from "antd";
import { useMutation } from "@tanstack/react-query";
import api from "../../utils/api";

const DEFAULT_TEMPLATE_FILENAME = "scholarship-acceptance-template";

const getFileNameFromContentDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) {
    return DEFAULT_TEMPLATE_FILENAME;
  }

  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  const fileName = utf8FileNameMatch?.[1] || fileNameMatch?.[1];

  return fileName ? decodeURIComponent(fileName.trim()) : DEFAULT_TEMPLATE_FILENAME;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getAcceptanceTemplate = async () => {
  const response = await api.get("/scholarship/acceptance-template", {
    responseType: "blob",
  });

  return {
    blob: response.data as Blob,
    fileName: getFileNameFromContentDisposition(
      response.headers["content-disposition"],
    ),
  };
};

export const useGetAcceptanceTemplateQuery = () => {
  const { notification } = App.useApp();

  const mutation = useMutation({
    mutationFn: getAcceptanceTemplate,
  });

  const downloadAcceptanceTemplate = async () => {
    try {
      const { blob, fileName } = await mutation.mutateAsync();
      downloadBlob(blob, fileName);

      notification.success({
        message: "Success",
        description: "Acceptance template downloaded successfully",
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description:
          error?.response?.data?.message || "Failed to download acceptance template",
      });
    }
  };

  return {
    ...mutation,
    downloadAcceptanceTemplate,
    isLoading: mutation.isPending,
  };
};

export default useGetAcceptanceTemplateQuery;
